import { faArrowRotateRight } from '@fortawesome/pro-light-svg-icons/faArrowRotateRight';
import { faCopy } from '@fortawesome/pro-light-svg-icons/faCopy';
import { faPencil } from '@fortawesome/pro-light-svg-icons/faPencil';
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons/faTrashAlt';
import { Box, useMediaQuery } from '@material-ui/core';
import { createStyles, makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import { AppointmentOrderItem } from '@spike/order-model';
import clsx from 'clsx';
import AppointmentData from 'components/Checkout2/UI/AppointmentData';
import { status as AppointmentStatusesIds } from '@spike/appointment-model';
import { FunctionComponent, useState } from 'react';
import StatusButton from '../StatusButton';
import ToolbarButton, { ToolbarOption } from '../UI/ToolbarButton';
import { useHasPremiumAccess } from 'hooks';
import { useSelector } from 'react-redux';
import Service from 'model/Service';
import { RootState } from 'store';

export interface AppointmentItemProps {
    appointmentItem: AppointmentOrderItem;
    editable: boolean;
    editingOrder: boolean;
    savingOther?: boolean;
    withoutOption?: boolean;
    onEdit?: () => void;
    onRebook?: () => void;
    onAddReport?: (appointmentId: number, businessAreaId: string) => void;
    onViewReport?: (appointmentId: number, businessAreaId: string) => void;
    onDelete?: () => void;
    onStartSavingStatus?: () => void;
    onStopSavingStatus?: () => void;
    className?: string;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            display: 'flex',
            flexDirection: 'column',
            padding: '15px 0px',
            width: '100%'
        },
        bodyContainer: {
            display: 'flex'
        },
        footerContainer: {
            width: '100%',
            paddingTop: 15,
            paddingLeft: 10
        },
        leftContainer: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            maxWidth: '65%',
            paddingRight: 15
        },
        rightContainer: {
            display: 'flex',
            flex: 1,
            height: '100%',
            justifyContent: 'flex-end',
            alignItems: 'center'
        },
        toolbarContainer: {
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            width: 40
        },
        emptyBox: {
            paddingRight: 9,
            borderLeft: '6px solid #ffffff',
            borderBottom: 'none'
        },
        box: {
            display: 'flex',
            marginLeft: -15,
            paddingRight: 15
        },
        withoutColor: {
            width: '97% !important'
        }
    })
);

const disableEditionStatuses = [
    AppointmentStatusesIds.CANCELLED,
    AppointmentStatusesIds.NO_SHOW,
    AppointmentStatusesIds.DECLINED,
    AppointmentStatusesIds.COMPLETED
];

const canceledStatuses = [AppointmentStatusesIds.CANCELLED, AppointmentStatusesIds.NO_SHOW];

export const AppointmentItem: FunctionComponent<AppointmentItemProps> = props => {
    const classes = useStyles();

    const theme = useTheme();
    const services = useSelector<RootState, Array<Service>>(state => state.services.services);
    const serviceColor = services.find(
        (serv: Service) => serv.id === props.appointmentItem.appointment.services[0].id
    )?.color;
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const hasReportsPremiumAccess = useHasPremiumAccess('groomming_reports');

    const [isSavingStatus, setIsSavingStatus] = useState(false);

    const disableEditionByStatus = disableEditionStatuses.includes(props.appointmentItem.appointment.status.id);

    const serviceWithReport = props.appointmentItem.appointment.services[0].hasReport;
    const reportAdded =
        props.appointmentItem.appointment.reports.dentalReportId ||
        props.appointmentItem.appointment.reports.groomingReportId;

    const appointmentId = props.appointmentItem.appointment.id!;
    const businessAreaId = props.appointmentItem.appointment.services[0].businessArea.id;

    const isCanceled = canceledStatuses.includes(props.appointmentItem.appointment.status.id);

    const toolbarOptions: Array<ToolbarOption> = [
        {
            id: 'edit',
            icon: faPencil,
            label: 'Edit Service',
            onClick: () => props.onEdit && props.onEdit()
        },
        {
            id: 'rebook',
            icon: faArrowRotateRight,
            label: 'Rebook',
            onClick: () => props.onRebook && props.onRebook()
        },
        {
            id: 'report',
            icon: faCopy,
            label: reportAdded ? 'View Report' : 'Add Report',
            onClick: () =>
                reportAdded
                    ? props.onViewReport && props.onViewReport(appointmentId, businessAreaId)
                    : props.onAddReport && props.onAddReport(appointmentId, businessAreaId)
        },
        {
            id: 'delete',
            icon: faTrashAlt,
            label: 'Delete',
            color: '#EF4F57',
            onClick: () => props.onDelete && props.onDelete()
        }
    ]
        .filter(option => props.editable || (option.id !== 'edit' && option.id !== 'delete'))
        .filter(option => !props.editingOrder || (option.id !== 'rebook' && option.id !== 'report'))
        .filter(option => props.onDelete || option.id !== 'delete')
        .filter(option => !disableEditionByStatus || (option.id !== 'edit' && option.id !== 'delete'))
        .filter(option => serviceWithReport || reportAdded || option.id !== 'report')
        .filter(option => hasReportsPremiumAccess || option.id !== 'report')
        .filter(option => !isCanceled || option.id !== 'report');

    const startSavingStatusHandler = () => {
        props.onStartSavingStatus && props.onStartSavingStatus();
        setIsSavingStatus(true);
    };

    const stopSavingStatusHandler = () => {
        props.onStopSavingStatus && props.onStopSavingStatus();
        setIsSavingStatus(false);
    };

    return (
        <Box className={classes.box}>
            <Box
                className={classes.emptyBox}
                style={{ borderLeftColor: serviceColor ?? '#ffffff' }}
            />
            <Box
                className={clsx(classes.container, props.className, {
                    [classes.withoutColor]: !serviceColor
                })}
            >
                <Box className={classes.bodyContainer}>
                    <Box className={classes.leftContainer}>
                        <AppointmentData appointmentItem={props.appointmentItem} />
                    </Box>
                    <Box className={classes.rightContainer}>
                        {!isMobile && !props.editingOrder && (
                            <StatusButton
                                appointment={props.appointmentItem.appointment}
                                reloadOrderAfterStatusChange={true}
                                onStartSaving={startSavingStatusHandler}
                                onStopSaving={stopSavingStatusHandler}
                                disabled={props.savingOther}
                            />
                        )}
                        {!props.withoutOption && (
                            <Box className={classes.toolbarContainer}>
                                {!isSavingStatus && (
                                    <ToolbarButton
                                        options={toolbarOptions}
                                        disabled={props.savingOther}
                                    />
                                )}
                            </Box>
                        )}
                    </Box>
                </Box>
                {isMobile && !props.editingOrder && (
                    <Box className={classes.footerContainer}>
                        <StatusButton
                            appointment={props.appointmentItem.appointment}
                            reloadOrderAfterStatusChange={true}
                            disabled={props.savingOther}
                        />
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default AppointmentItem;
